// Bridges imperative `BoardState` events into the central `WhiteboardBus.domain$`
// Subject. One of four state-stream adapters whitelisted by the
// `single-publisher-invariant` ESLint rule (file suffix match).
//
// Subscribes to the 4 mutation events of BoardState in the constructor and
// forwards each into the typed `DomainEvent` discriminated union on `bus.domain$`.
// `dispose()` unsubscribes all listeners — MUST be called from the host's
// `disconnectedCallback` BEFORE `bus.destroy()` (TR9 / FC-6 ordering).

import type { BoardState } from '../state/board-state.js';
import { objectId } from './branded-types.js';
import type { WhiteboardBus } from './whiteboard-bus.js';

type Unsubscribe = () => void;

export class BoardStateBusAdapter {
    readonly #unsubscribes: readonly Unsubscribe[];

    constructor(state: BoardState, bus: WhiteboardBus) {
        this.#unsubscribes = [
            state.on('object-added', ({ object }) => {
                bus.domain$.next({ type: 'object-added', payload: object });
            }),
            state.on('object-updated', ({ object }) => {
                bus.domain$.next({ type: 'object-updated', id: objectId(object.id), payload: object });
            }),
            state.on('object-removed', ({ id }) => {
                bus.domain$.next({ type: 'object-removed', id: objectId(id) });
            }),
            state.on('object-reordered', ({ id }) => {
                bus.domain$.next({ type: 'object-reordered', id: objectId(id) });
            }),
            state.on('state-replaced', ({ objects }) => {
                bus.domain$.next({ type: 'state-replaced', payload: objects });
            }),
        ];
    }

    /** Remove all 5 BoardState listeners. Idempotent. */
    dispose(): void {
        for (const unsubscribe of this.#unsubscribes) unsubscribe();
    }
}
